import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import Tooltip from "@mui/material/Tooltip";
import "./DataEstateAccessCard.scss";
import { getAccessBuckets, getTotalFiles } from "../Overviewadapters";

/**
 * File Archival Data (compact) - from /dataEstate/overview -> card5
 *   { totalFiles, duplicateFiles, accessBuckets: [{ accessRange, fileCount }] }
 *
 * accessBuckets are CUMULATIVE ("accessed within N months"; within-12 + "> 12 months" = total),
 * so the rows show them as they come and the slim stacked bar converts them to
 * non-overlapping slices so it adds up to 100%.
 *
 * Layout is vertical (ring + legend on top, one slim bar, then compact rows) so it fits a
 * narrow card - no per-row bar graphs.
 */

const fmtCount = (n = 0) => {
  const v = Number(n) || 0;
  const trim = (x) => String(Number(x.toFixed(1)));
  if (v >= 1e9) return `${trim(v / 1e9)}B`;
  if (v >= 1e6) return `${trim(v / 1e6)}M`;
  if (v >= 1e3) return `${trim(v / 1e3)}k`;
  return String(v);
};

const fmtPct = (v) => {
  if (v > 0 && v < 0.1) return "<0.1%";
  return `${v >= 10 ? Math.round(v) : Math.round(v * 10) / 10}%`;
};

// "Files Accessed in 3 Months" -> 3 ; "Files Accessed > 12 Months (...)" -> archive
const parseBucket = (b) => {
  const text = String(b.accessRange || "").toLowerCase();
  const n = Number((text.match(/(\d+)\s*(month|year)/) || [])[1]);
  const months = text.includes("year") ? n * 12 : n;
  return {
    months: Number.isFinite(months) ? months : null,
    archive: text.includes(">"),
    count: Number(b.fileCount) || 0,
  };
};

const COLORS = ["#3a2bb8", "#5b45d9", "#8b78ee"]; // 3, 6, 12 months
const ARCHIVE_COLOR = "#f59e0b";

/* duplicate ring */
const RING = 88;
const STROKE = 12;
const R = (RING - STROKE) / 2;
const C = 2 * Math.PI * R;

export default function DataEstateAccessCard() {
  const overview = useSelector((state) => state.dataTagReducer.dataEstateOverview);

  const model = useMemo(() => {
    const parsed = getAccessBuckets(overview).map(parseBucket);
    const within = parsed.filter((b) => !b.archive && b.months).sort((a, b) => a.months - b.months);
    const archive = parsed.find((b) => b.archive);
    if (!within.length && !archive) return null;

    const archiveCount = archive?.count || 0;
    const total =
      Number(overview?.card5?.totalFiles) ||
      getTotalFiles(overview) ||
      (within.length ? within[within.length - 1].count : 0) + archiveCount;
    const duplicate = Number(overview?.card5?.duplicateFiles) || 0;
    const pctOf = (n) => (total ? (n / total) * 100 : 0);

    const rows = within.map((b, i) => ({
      key: b.months,
      label: `${b.months} months`,
      count: b.count,
      pct: pctOf(b.count),
      color: COLORS[i % COLORS.length],
    }));
    rows.push({
      key: "archive",
      label: "> 12 months",
      count: archiveCount,
      pct: pctOf(archiveCount),
      color: ARCHIVE_COLOR,
      archival: true,
    });

    // non-overlapping slices for the slim stacked bar
    let prev = 0;
    const slices = rows.map((r) => {
      if (r.archival) return { key: r.key, label: r.label, value: r.count, color: r.color };
      const value = Math.max(0, r.count - prev);
      prev = Math.max(prev, r.count);
      return { key: r.key, label: r.label, value, color: r.color };
    });

    return { total, duplicate, duplicatePct: pctOf(duplicate), rows, slices };
  }, [overview]);

  if (!model) return <div className="no-data">No data available</div>;

  const dash = Math.max((Math.min(model.duplicatePct, 100) / 100) * C, model.duplicate ? 2 : 0.01);

  return (
    <div className="dai-card">
      {/* duplicate ring + legend */}
      <div className="dai-top">
        <Tooltip title={`Duplicate files: ${fmtPct(model.duplicatePct)}`} arrow>
          <div className="dai-ring">
            <svg viewBox={`0 0 ${RING} ${RING}`} role="img" aria-label={`${fmtPct(model.duplicatePct)} duplicate files`}>
              <circle cx={RING / 2} cy={RING / 2} r={R} className="dai-ring-track" strokeWidth={STROKE} />
              <circle
                cx={RING / 2}
                cy={RING / 2}
                r={R}
                className="dai-ring-fill"
                strokeWidth={STROKE}
                strokeDasharray={`${dash} ${C}`}
                transform={`rotate(-90 ${RING / 2} ${RING / 2})`}
              />
            </svg>
            <span className="dai-ring-pct">{fmtPct(model.duplicatePct)}</span>
          </div>
        </Tooltip>

        <div className="dai-legend">
          <div className="dai-legend-row" title={`${model.duplicate.toLocaleString("en-US")} duplicate files`}>
            <span className="dai-line duplicate" />
            <b>{fmtCount(model.duplicate)}</b>
            <span className="dai-legend-label">Duplicate</span>
          </div>
          <div className="dai-legend-row" title={`${model.total.toLocaleString("en-US")} total files`}>
            <span className="dai-line total" />
            <b>{fmtCount(model.total)}</b>
            <span className="dai-legend-label">Total</span>
          </div>
        </div>
      </div>

      {/* files accessed */}
      <div className="dai-heading">Files Accessed</div>

      <div className="dai-bar" role="img" aria-label="Share of files by last access">
        {model.slices.map((s) => (
          <span
            key={s.key}
            className="dai-bar-seg"
            title={`${s.label}: ${s.value.toLocaleString("en-US")}`}
            style={{
              flex: `${Math.max(model.total ? s.value / model.total : 0, s.value ? 0.012 : 0)} 1 0`,
              background: s.color,
            }}
          />
        ))}
      </div>

      <div className="dai-tiles">
        {model.rows.map((r) => (
          <div
            key={r.key}
            className={`dai-tile ${r.archival ? "archival" : ""}`}
            title={`${r.label}: ${r.count.toLocaleString("en-US")} files (${fmtPct(r.pct)})`}
          >
            <span className="dai-tile-label">
              <span className="dai-dot" style={{ background: r.color }} />
              {r.label.replace(/ months?$/, "M")}
            </span>
            <b className="dai-tile-value">{fmtCount(r.count)}</b>
            <span className="dai-tile-pct">{fmtPct(r.pct)}</span>
          </div>
        ))}
      </div>
      <div className="dai-note">
        <span className="dai-dot" style={{ background: ARCHIVE_COLOR }} />
        &gt; 12M = Retention &amp; Archival
      </div>
    </div>
  );
}